import React, { useState, useEffect } from 'react';
import { Textarea, Button } from "../ui";
import { useTheme } from '../../context/ThemeContext';
import {
  getCardBackground,
  getPrimaryText,
  getSecondaryText,
  getMutedText,
  getLightBorder
} from '../../utils/themeUtils';
import {
  submitSupportTicket,
  SupportTicketRequest
} from '../../api/supportTicketAPI';
import { linkErrorToTicket } from '../../api/errorLogAPI';
import { getTenantId } from '../../api/apiHelpers';
import { toast } from 'react-toastify';
import { captureError } from '../../utils/sentry';
import { safeGetItem, safeGetObject } from '@mediatechgroup/app-version';

export interface ErrorContext {
  errorLogId?: string;
  errorMessage: string;
  errorCode?: string;
  pageUrl?: string;
  pageTitle?: string;
  actionContext?: string;
  componentName?: string;
  apiEndpoint?: string;
  apiMethod?: string;
  responseStatus?: number;
  responseError?: Record<string, unknown>;
  additionalContext?: Record<string, unknown>;
}

interface ReportIssueModalProps {
  isOpen: boolean;
  onClose: () => void;
  errorContext: ErrorContext;
  onSuccess?: () => void;
}

const ReportIssueModal: React.FC<ReportIssueModalProps> = ({
  isOpen,
  onClose,
  errorContext,
  onSuccess
}) => {
  const { isDarkMode } = useTheme();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [additionalDetails, setAdditionalDetails] = useState('');

  // Get user details from localStorage
  const userDetails: Record<string, any> = safeGetObject('userDetails');
  const tenantSettings: Record<string, any> = safeGetObject('settings');
  const authSource = safeGetItem('authSource') || '';
  const tenantId = getTenantId() || '';
  const token = safeGetItem('authToken');

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setAdditionalDetails('');
    }
  }, [isOpen]);

  // Don't render if not open
  if (!isOpen) return null;

  // Build the technical details string
  const getTechnicalDetails = () => {
    const details: string[] = [];

    if (errorContext.pageUrl) {
      details.push(`Page: ${errorContext.pageUrl}`);
    }
    if (errorContext.actionContext) {
      details.push(`Action: ${errorContext.actionContext}`);
    }
    if (errorContext.componentName) {
      details.push(`Component: ${errorContext.componentName}`);
    }
    if (errorContext.apiEndpoint) {
      details.push(`API: ${errorContext.apiMethod || 'GET'} ${errorContext.apiEndpoint}`);
    }
    if (errorContext.responseStatus) {
      details.push(`Status: ${errorContext.responseStatus}`);
    }
    if (errorContext.responseError) {
      details.push(`Error Response: ${JSON.stringify(errorContext.responseError, null, 2)}`);
    }
    if (errorContext.additionalContext) {
      details.push(`Additional Context: ${JSON.stringify(errorContext.additionalContext, null, 2)}`);
    }

    return details.join('\n');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Build the full description with error context
      const fullDescription = `
**Error Message:**
${errorContext.errorMessage}

**Technical Details:**
${getTechnicalDetails()}

**User's Additional Details:**
${additionalDetails || 'No additional details provided.'}
`.trim();

      const ticketData: SupportTicketRequest = {
        name: `${userDetails.first_name || ''} ${userDetails.last_name || ''}`.trim() || 'Unknown User',
        email: userDetails.email || '',
        subject: `Error Report: ${errorContext.errorMessage.substring(0, 50)}${errorContext.errorMessage.length > 50 ? '...' : ''}`,
        category: 'bug',
        priority: 'high',
        description: fullDescription,
        attachments: [],
        // Hidden fields
        role: userDetails.role || '',
        tenantId: tenantId,
        tenantName: tenantSettings.tenant_name || tenantSettings.businessName || '',
        userId: userDetails.id || '',
        authSource: authSource,
      };

      const response = await submitSupportTicket(token, ticketData);

      if (response.success && response.data?.ticketId) {
        // If we have an error log ID, link it to the ticket
        if (errorContext.errorLogId) {
          await linkErrorToTicket(token, errorContext.errorLogId, response.data.ticketId);
        }

        toast.success('Issue reported successfully! Our team will look into it.');
        onSuccess?.();
        onClose();
      } else {
        toast.error(response.message || 'Failed to submit report');
      }
    } catch (error) {
      captureError(error, { component: "ReportIssueModal", operation: "submitReport" });
      toast.error('Failed to submit issue report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className={`relative w-full max-w-lg mx-4 rounded-xl shadow-2xl ${getCardBackground(isDarkMode)} border ${getLightBorder(isDarkMode)}`}
      >
        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 border-b ${getLightBorder(isDarkMode)}`}>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
              <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div>
              <h2 className={`text-lg font-semibold ${getPrimaryText(isDarkMode)}`}>
                Report Issue
              </h2>
              <p className={`text-sm ${getMutedText(isDarkMode)}`}>
                Help us fix this problem
              </p>
            </div>
          </div>
          <Button
            type="button"
            variant="neutral"
            size="sm"
            iconOnly
            aria-label="Close"
            onClick={onClose}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </Button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
            {/* Error Summary */}
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className={`text-sm font-medium ${getPrimaryText(isDarkMode)}`}>
                Error Encountered:
              </p>
              <p className={`text-sm mt-1 ${getSecondaryText(isDarkMode)}`}>
                {errorContext.errorMessage}
              </p>
            </div>

            {/* Technical Details (Read-only) */}
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${getSecondaryText(isDarkMode)}`}>
                Technical Details (auto-captured)
              </label>
              <div
                className={`w-full p-3 rounded-lg text-sm font-mono bg-gray-100 dark:bg-gray-800 ${getMutedText(isDarkMode)} max-h-32 overflow-y-auto whitespace-pre-wrap`}
              >
                {getTechnicalDetails() || 'No additional technical details available.'}
              </div>
            </div>

            {/* Additional Details */}
            <div>
              <Textarea
                label="What were you trying to do? (optional)"
                value={additionalDetails}
                onChange={(e) => setAdditionalDetails(e.target.value)}
                placeholder="Describe what you were doing when this error occurred..."
                rows={3}
                className="resize-none"
              />
            </div>

            {/* Info Note */}
            <div className={`flex items-start gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20`}>
              <svg className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className={`text-sm ${getSecondaryText(isDarkMode)}`}>
                This report will be sent to our support team. We'll use the captured technical details to investigate and fix this issue.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className={`flex items-center justify-end gap-3 px-6 py-4 border-t ${getLightBorder(isDarkMode)}`}>
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2 rounded-lg border ${getLightBorder(isDarkMode)} ${getSecondaryText(isDarkMode)} hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Submitting...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  Submit Report
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReportIssueModal;
